import { z } from 'zod';
import {
  COUNTRIES,
  type ClassifierAdapter,
  type CountryConfig,
  type RegionCatalog,
} from './types.js';
import { isKnownTimeZone } from './time-zone.js';

// Grows alongside CountryConfig. Validates CONTENT that TypeScript can't prove.
export const countryConfigSchema = z
  .object({
    code: z.enum(COUNTRIES),
    locales: z.array(z.string()).nonempty(),
    defaultLocale: z.string(),
    regions: z.custom<RegionCatalog>(
      (v) => typeof (v as { localize?: unknown })?.localize === 'function',
    ),
    currency: z.object({
      code: z.string().length(3),
      symbol: z.string().min(1),
      compactUnits: z.record(z.array(z.string())),
      // Optional (CL): the singular of each unit, for a figure of exactly 1.
      compactUnitsOne: z.record(z.array(z.string())).optional(),
      words: z.record(z.string().min(1)),
      // Optional Intl locale for budget digits (ES 'es-ES'); absent on UZ/KZ.
      numberLocale: z.string().min(2).optional(),
    }),
    brand: z.object({
      name: z.string().min(1),
      siteUrl: z.string().url(),
      supportEmail: z.string().email(),
      // Published human Telegram contact, or null for "show none". Separate from
      // `messaging.telegram` (the bot) — see BrandConfig.
      supportTelegram: z
        .object({ handle: z.string().regex(/^@\w+$/), url: z.string().url() })
        .nullable(),
      // Whether the operator's phone / WhatsApp line is published.
      publishPhone: z.boolean(),
    }),
    // Country display name per UI locale, in nominative/genitive/prepositional cases.
    countryName: z.record(
      z.object({ nom: z.string().min(1), gen: z.string().min(1), prep: z.string().min(1) }),
    ),
    // Web ИНН/БИН form descriptor (label + digit count). `alphanumeric.pattern` (ES
    // NIF) must compile as a regex, or every form on the deployment would throw.
    companyIdForm: z.object({
      label: z.string().min(1),
      digits: z.number().int().positive(),
      alphanumeric: z
        .object({
          pattern: z.string().refine((p) => {
            try {
              new RegExp(p);
              return p.startsWith('^') && p.endsWith('$');
            } catch {
              return false;
            }
          }, 'companyIdForm.alphanumeric.pattern must be an anchored, valid regex'),
        })
        .optional(),
      keepsTypedName: z.boolean().optional(),
      // Optional (CL): the id is a RUT, written `digits-DV`; absent on UZ/KZ/ES.
      format: z.literal('rut').optional(),
      // Optional (CL): which ids are natural persons, and the word a teaser prints
      // instead of their name; absent on UZ/KZ/ES.
      naturalPerson: z
        .object({
          isPerson: z.custom<(id: string) => boolean>((v) => typeof v === 'function'),
          label: z.string().min(1),
        })
        .optional(),
    }),
    phonePlaceholder: z.string().min(1),
    // Optional IANA zone (ES 'Europe/Madrid'); absent on UZ/KZ. A typo would make
    // every zoned date Invalid — refused at boot instead.
    timeZone: z.string().refine(isKnownTimeZone, 'timeZone must be an IANA zone name').optional(),
    // Meta Pixel id + GA4 measurement id baked into index.html per country. Either
    // may be '' (disables it for the deployment — ES has neither yet); a non-empty
    // pixel id must be the numeric id Meta issues.
    analytics: z.object({
      metaPixelId: z.string().regex(/^(\d+)?$/),
      ga4MeasurementId: z.string(),
      // Optional ES switch (absent = as before): one wall hit per lot view.
      paywallOncePerLot: z.boolean().optional(),
      // Optional ES switch: cookieless PostHog before the banner is answered.
      cookielessBeforeConsent: z.boolean().optional(),
      // Optional share of consented sessions PostHog records (absent = 0.1, as before).
      replaySampleRate: z.number().min(0).max(1).optional(),
    }),
    // Optional editorial rules (ES: Castilian titles for Catalan/Galician/Basque lots, and
    // short readable titles for long or opaque ones).
    content: z
      .object({
        castilianTitles: z.boolean().optional(),
        readableTitles: z.boolean().optional(),
      })
      .optional(),
    // Which messaging channels the deployment offers. `telegram` gates the web's
    // Telegram surfaces and the prod build's VITE_TELEGRAM_BOT_USERNAME requirement.
    // `telegramBot` is the bare username (no @), `telegramChannel` the advertised
    // channel or null. A bot username without the bot switched on (or the reverse)
    // is a half-wired deployment and is refused below.
    messaging: z.object({
      telegram: z.boolean(),
      telegramBot: z
        .string()
        .regex(/^[A-Za-z]\w{3,30}bot$/i)
        .nullable(),
      telegramChannel: z
        .object({ handle: z.string().regex(/^@\w+$/), url: z.string().url() })
        .nullable(),
      emailFirstAlert: z.boolean(),
      // Optional: absent = alerts need a confirmed (or paying) address (UZ/KZ).
      emailWithoutConfirmation: z.boolean().optional(),
      // Optional: absent = the create-time first lot reads the request (UZ/KZ).
      singleFirstAlert: z.boolean().optional(),
      ownerAlertsByEmail: z.boolean().optional(),
    }),
    // Analytics/UX surfaces gated per deployment. `priceIntel` gates /market,
    // /prices and the kind filter (all built on the ИКПУ taxonomy + price history).
    features: z.object({
      priceIntel: z.boolean(),
      teamPlans: z.boolean(),
      corporateContactGate: z.boolean(),
      // Optional: absent = gov lots need a buyer contact to be browsed (UZ/KZ/ES).
      govContactGate: z.boolean().optional(),
      conversionLandings: z.boolean(),
      aiOnboarding: z.boolean(),
      honestTeasers: z.boolean(),
      supplyScope: z.boolean(),
      // Optional: absent = both side sections (/ifi, /supply) served, as before (UZ/KZ).
      sections: z.array(z.enum(['ifi', 'supply'])).optional(),
      // Optional: absent = no cookie banner, trackers load as before (UZ/KZ).
      cookieConsent: z.boolean().optional(),
      // Optional: absent = documents, buyer contact and source link stay Pro (UZ/KZ).
      publicSourceData: z.boolean().optional(),
      // Optional: absent = the legacy rule — UZ walls the award outcome, KZ does not.
      outcomeWall: z.boolean().optional(),
      // Optional: absent = no «Próximos vencimientos» page, row or endpoint (UZ/KZ).
      expiringContracts: z.boolean().optional(),
      // Optional: absent = the goods/works/services tabs follow `priceIntel` (UZ/KZ).
      kindTabs: z.boolean().optional(),
      // Optional: absent = the spotlight card and the save button as before (UZ/KZ).
      alertLanding: z.boolean().optional(),
      // Optional: absent = the Pro card and the lot's wall in their order (UZ/KZ).
      marketFirstPitch: z.boolean().optional(),
      // Optional: absent = the market teaser blurs every value and sends none (UZ/KZ).
      freeBidderMedian: z.boolean().optional(),
      // Optional: absent = the AI step keeps its company-id door, no codes offer (UZ/KZ).
      aiCompanyStep: z.boolean().optional(),
      // Optional: absent = a guest pays via /login, onboarding opens on /plans too (UZ/KZ).
      purchaseFirst: z.boolean().optional(),
      // Optional: absent = no way back to the AI step, «Изменить» opens the editor (UZ/KZ).
      aiOnboardingReentry: z.boolean().optional(),
      // Optional: absent = a page past the last one is the plain empty list (UZ/KZ).
      pastLastPage: z.boolean().optional(),
      // Optional: absent = /login leaves empty/invalid fields to the browser (UZ/KZ).
      loginOwnErrors: z.boolean().optional(),
      // Optional: absent = saved-search links and the first lot use scalar params (UZ/KZ).
      alertCriteriaLink: z.boolean().optional(),
      // Optional: absent = a saved search's words AND with its codes (UZ/KZ).
      codesOverTerms: z.boolean().optional(),
      // Optional: absent = code chips are named from the browse list (UZ/KZ).
      codeNamesFromTable: z.boolean().optional(),
      // Optional: absent = an AI proposal's «per day» counts open lots by ingest date (UZ/KZ).
      ratePerDayByPublication: z.boolean().optional(),
      // Optional: absent = the sidebar's guest link stashes nothing, the sheet never waits (UZ/KZ).
      guestSearchCarried: z.boolean().optional(),
      // Optional: absent = a signed-in save starts with a blank budget floor (UZ/KZ/ES).
      saveSearchCarriesBudget: z.boolean().optional(),
      // Optional: absent = any checkout stash sends a sign-in to /plans for two hours (UZ/KZ).
      purchaseIntentScoped: z.boolean().optional(),
      // Optional: absent = a search's category unions with its CPV codes in the match (UZ/KZ).
      codesOverCategory: z.boolean().optional(),
      // Optional: absent = «Omitir» on the company step only closes the sheet (UZ/KZ).
      companySkipRemembered: z.boolean().optional(),
      // Optional: absent = a refused code is recognised by its `code` only, never its message (UZ/KZ).
      refusedCodeCleared: z.boolean().optional(),
      // Optional: absent = /billing/return thanks any visitor for a payment (UZ/KZ).
      neutralBillingReturn: z.boolean().optional(),
      // Optional: absent = the company step prints «ya vinculado a otra cuenta» beside «guardado» (UZ/KZ).
      companyLinkNoticeHidden: z.boolean().optional(),
      // Optional: absent = no «con los filtros activos» line over a keyword search (UZ/KZ).
      searchScopeLine: z.boolean().optional(),
    }),
    // How the deployment takes money: the checkout shape, which plan families
    // are sold, the marks by the pay button, and who accepts the card.
    payments: z
      .object({
        checkout: z.enum(['local', 'stripe']),
        yearlyPlans: z.boolean(),
        bankInvoice: z.boolean(),
        ipPlan: z.boolean(),
        marks: z
          .array(
            z.enum([
              'payme',
              'click',
              'octobank',
              'uzcard',
              'humo',
              'visa',
              'mastercard',
              'stripe',
            ]),
          )
          .nonempty(),
        cardPayee: z
          .object({
            name: z.string().min(1),
            address: z.string().min(1),
            phone: z.string().min(1),
            processor: z.string().min(1),
          })
          .nullable(),
        // Optional: absent = no Stripe Tax and no tax line (UZ/KZ/ES).
        stripeTax: z.object({ taxBehavior: z.enum(['exclusive', 'inclusive']) }).optional(),
        // Optional: absent = Stripe asks for nothing beyond the card (UZ/KZ).
        // `inCardSheet` (CL): the card sheet shows the optional invoice block.
        checkoutCollects: z
          .object({
            taxId: z.boolean(),
            billingAddress: z.boolean(),
            inCardSheet: z.boolean().optional(),
          })
          .optional(),
        // Optional: absent = no VAT wording on the prices (UZ/KZ). ES: «IVA no incluido».
        vatNotCharged: z.boolean().optional(),
        // Optional: absent = no «IVA incluido» wording (UZ/KZ/ES). CL: 0.19.
        vatIncludedRate: z.number().gt(0).lt(1).optional(),
      })
      // «VAT is in the price» contradicts both «no VAT» and «Stripe Tax adds it».
      .refine(
        (p) => p.vatIncludedRate === undefined || (!p.vatNotCharged && !p.stripeTax),
        'payments.vatIncludedRate contradicts payments.vatNotCharged / payments.stripeTax',
      )
      // «No VAT is charged» and «Stripe Tax charges it» cannot both be true.
      .refine(
        (p) => !(p.stripeTax && p.vatNotCharged),
        'payments.vatNotCharged contradicts payments.stripeTax',
      )
      // Stripe Tax computes from the billing address and reverse-charges on the
      // tax id: a tax config without both would open pages that cannot tax.
      .refine(
        (p) =>
          !p.stripeTax ||
          (p.checkoutCollects?.taxId === true && p.checkoutCollects.billingAddress === true),
        'payments.stripeTax needs payments.checkoutCollects { taxId: true, billingAddress: true }',
      ),
    // Empty allowed: a not-yet-launched country (e.g. kz before its adapters exist)
    // legitimately ingests nothing. A misconfigured live country surfaces as "0 sources
    // enabled" in ingestion health, not a boot crash.
    sourceKeys: z.array(z.string()),
    // Direct contracts kept out of the concentration reads. Markers are matched
    // as lower-case substrings, so an upper-case or a too-short one would
    // silently match nothing (or nearly everything) — refused here.
    competition: z.object({
      nonCompetitiveSources: z.array(z.string().min(1)),
      nonCompetitiveMethodMarkers: z.array(
        z
          .string()
          .min(5)
          .refine((m) => m === m.toLowerCase(), 'method markers must be lower-case'),
      ),
      unknownMethodIsCompetitive: z.boolean(),
    }),
    companyId: z.object({
      label: z.string().min(1),
      // Deliberately NOT a content smoke test (e.g. "accepts a seeded 9-digit
      // positive"): company-id shape is country-specific (UZ ИНН is 9 digits;
      // a future country's scheme may not be), and this schema is shared
      // across all countries — a fixture shaped for one would wrongly fail
      // another's valid config. `typeof === 'function'` is the same content
      // ceiling `regions.localize` above already accepts for the same reason.
      validate: z.custom<(id: string) => boolean>((v) => typeof v === 'function'),
      // Goes into SQL as a literal (`company-suggest.ts`): anchored, a valid
      // regex, and no quote that could end the literal.
      suggestPattern: z
        .string()
        .refine((p) => {
          try {
            new RegExp(p);
            return p.startsWith('^') && p.endsWith('$') && !p.includes("'");
          } catch {
            return false;
          }
        }, 'companyId.suggestPattern must be an anchored, valid regex without quotes')
        .optional(),
      nameSearch: z.literal('es').optional(),
      payoff: z.literal('es').optional(),
    }),
    // Same `typeof === 'function'` content ceiling as `regions`/`companyId`
    // above: an object exposing both `resolve` and `classify` functions. The
    // rule tables are country-specific, so a behavioural fixture here would
    // wrongly fail another country's classifier.
    classifier: z.custom<ClassifierAdapter>(
      (v) =>
        typeof (v as { resolve?: unknown })?.resolve === 'function' &&
        typeof (v as { classify?: unknown })?.classify === 'function',
    ),
    companyIndex: z.object({
      minCompetitiveAwards: z.number().int().positive(),
      windowMonths: z.number().int().positive(),
      requireActiveStatus: z.boolean(),
      indexableId: z.custom<(id: string) => boolean>((v) => typeof v === 'function').optional(),
    }),
    // Credible award-price band in local currency (the plausibility sweep).
    awardPlausibility: z
      .object({ floor: z.number().positive(), ceiling: z.number().positive() })
      .refine((p) => p.floor < p.ceiling, 'awardPlausibility.floor must be below ceiling'),
    // Optional: absent = the shared sign-in rule (UZ/KZ). ES: an existing account gets a code.
    auth: z.object({ codeRequiredForExisting: z.boolean().optional() }).optional(),
    // Optional: absent = the home lists every source (UZ/KZ). ES leaves the consultations out.
    home: z.object({ excludeSources: z.array(z.string().min(1)) }).optional(),
    // Optional: absent = the email assistant answers and hands over, never acts (UZ/KZ).
    support: z.object({ assistantManagesSearches: z.boolean().optional() }).optional(),
  })
  .refine((c) => c.locales.includes(c.defaultLocale), 'defaultLocale must be one of locales')
  .refine(
    (c) => c.messaging.telegram === (c.messaging.telegramBot !== null),
    'messaging.telegramBot must be set exactly when messaging.telegram is on',
  );

export function assertCountryConfig(c: CountryConfig): void {
  const r = countryConfigSchema.safeParse(c);
  if (!r.success) throw new Error(`Invalid CountryConfig '${String(c.code)}': ${r.error.message}`);
}
